import type { App } from '@grafikr/components';

import intersectionLoader from 'utils/loaders/intersection';
import onEvent from 'utils/loaders/on-event';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import onUrlParam from 'utils/loaders/on-url-param';
import geoPopup from 'components/geo-popup';

import header from 'layout/header';
import horizontalScroll from 'layout/horizontal-scroll';
import liveSearch from 'layout/live-search';

export default (app: App) => {
  app.add({
    // Layout
    'geo-popup': geoPopup,
    header,
    'horizontal-scroll': horizontalScroll,
    'live-search': liveSearch,
    'mobile-menu': [
      onElementInteraction,
      () => import(/* webpackChunkName: "mobile-menu" */ 'layout/mobile-menu'),
    ],

    // Components
    accordion: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-accordion" */ 'components/accordion'),
    ],
    'cookie-button': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-cookie-button" */ 'components/cookie-button'),
    ],
    'copy': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    'drawer-button': [
      onElementInteraction,
      () => import(/* webpackChunkName: "component-drawer-button" */ 'components/drawer-button'),
    ],
    'address-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-address-form" */ 'components/address-form'),
    ],
    'cart-drawer': [
      [onEvent, onElementInteraction, onUrlParam],
      () => import(/* webpackChunkName: "component-cart-drawer" */ 'components/cart-drawer'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    'responsive-video': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-responsive-video" */ 'components/responsive-video'),
    ],
    slider: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-slider" */ 'components/slider'),
    ],
    tabs: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-tabs" */ 'components/tabs'),
    ],
    'terms-alert': [
      [onEvent],
      () => import(/* webpackChunkName: "component-terms-alert" */ 'components/terms-alert'),
    ],
    'text-sections-with-images': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-text-sections-with-images" */ 'components/text-sections-with-images'
        ),
    ],

    // Pages
    404: [intersectionLoader, () => import(/* webpackChunkName: "404" */ 'pages/404/main')],
    blog: [intersectionLoader, () => import(/* webpackChunkName: "blog" */ 'pages/blog/blog')],
    'collection-products': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "collection-products" */ 'pages/collection/collection-products'
        ),
    ],
    'collection-seo': [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-seo" */ 'pages/collection/collection-seo'),
    ],
    designers: [
      intersectionLoader,
      () => import(/* webpackChunkName: "designers" */ 'pages/designers/designers'),
    ],
    'gift-card-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "gift-card-form" */ 'pages/product/gift-card-form'),
    ],
    product: [
      intersectionLoader,
      () => import(/* webpackChunkName: "product" */ 'pages/product/product'),
    ],
    'product-media': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-media" */ 'pages/product/media'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    'product-configurator': [
      onEvent,
      () => import(/* webpackChunkName: "product-configurator" */ 'pages/product/configurator'),
    ],
    'product-specs-list': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-specs-list" */ 'pages/product/specs-list'),
    ],
    'search-page': [
      intersectionLoader,
      () => import(/* webpackChunkName: "search-page" */ 'pages/search/search-page'),
    ],
  });
};
